/**
 * @module components/trends
 *
 * The horizon section: a technology radar plus a synced list.
 *
 * The radar artwork is an SVG scene; the plots are real `<button>` elements
 * positioned over it, so each trend is focusable and clickable rather than being
 * a decorative dot. Plots and list rows highlight each other, which is what lets
 * a reader move between the picture and the detail without losing their place.
 */

import { MEDIA, createMotionContext } from "../core/gsap-setup.js";
import { qs, qsa, on } from "../core/dom.js";
import { watchReveal } from "../animations/scrollReveal.js";
import { TRENDS, RINGS, QUADRANTS, TRENDS_SUMMARY } from "../data/trends.js";
import { icon } from "../svg/icons.js";
import { trendRadar } from "../svg/scenes.js";

/** viewBox size of the radar scene — must match `trendRadar()`'s default. */
const VIEW = 620;
/** Outer radius of the radar, in viewBox units (matches `trendRadar`). */
const MAX_R = Math.round(VIEW * 0.4214);

/**
 * Convert a trend's ring/angle into a percentage position inside the scene box.
 *
 * @param {import("../data/trends.js").Trend} trend
 * @returns {{ left: string, top: string }}
 */
export function plotPosition(trend) {
  const ring = RINGS.find((entry) => entry.id === trend.ring) ?? RINGS[0];
  const radiusPercent = ((MAX_R * ring.radius) / VIEW) * 100;
  const theta = ((trend.angle - 90) * Math.PI) / 180;
  const left = 50 + radiusPercent * Math.cos(theta);
  const top = 50 + radiusPercent * Math.sin(theta);
  return { left: `${left.toFixed(2)}%`, top: `${top.toFixed(2)}%` };
}

/**
 * Render one list row.
 *
 * @param {import("../data/trends.js").Trend} trend
 * @returns {string} Markup.
 */
function trendMarkup(trend) {
  return `
    <li class="trend-item" data-trend="${trend.id}" style="--trend-accent:${trend.accent}">
      <div class="trend-item__head">
        <h3 class="trend-item__name">${trend.name}</h3>
        <span class="trend-item__ring">${trend.ring} · ${trend.quadrant}</span>
        <span class="trend-item__horizon">${trend.horizon}</span>
      </div>
      <p class="trend-item__body">${trend.body}</p>
      <p class="trend-item__sowhat">${icon("compass", { size: 15 })}<span>${trend.sowhat}</span></p>
    </li>`;
}

/**
 * @returns {() => void} Teardown.
 */
export function initTrends() {
  const root = qs("[data-trends]");
  if (!root) return () => {};

  const eyebrow = qs("[data-trends-eyebrow]", root);
  const title = qs("[data-trends-title]", root);
  const lede = qs("[data-trends-lede]", root);
  if (eyebrow) eyebrow.textContent = TRENDS_SUMMARY.eyebrow;
  if (title) title.textContent = TRENDS_SUMMARY.title;
  if (lede) lede.textContent = TRENDS_SUMMARY.lede;

  /* --- Radar ------------------------------------------------------------- */

  const sceneMount = qs("[data-radar-scene]", root);
  if (sceneMount) {
    sceneMount.innerHTML = trendRadar({
      rings: RINGS.map((ring) => ({ id: ring.id, label: ring.label, radius: ring.radius })),
      quadrants: QUADRANTS,
      accent: "#5fd4c4",
      secondary: "#d8a657",
    });

    /*
     * The plots are HTML buttons, not SVG shapes, so that each trend is
     * keyboard-focusable and carries an accessible name. They live in an
     * absolutely-positioned overlay on top of the SVG, sharing the same
     * coordinate system (the scene box is square, so percentages line up).
     */
    const plotLayer = document.createElement("div");
    plotLayer.className = "radar-plots-overlay";
    plotLayer.innerHTML = TRENDS.map((trend) => {
      const position = plotPosition(trend);
      return `
        <button type="button" class="radar-plot" data-plot="${trend.id}"
                style="left:${position.left};top:${position.top};--plot-accent:${trend.accent}"
                aria-label="${trend.name} — ${trend.ring} ring, ${trend.quadrant} quadrant">
          <span class="radar-plot__label" aria-hidden="true">${trend.name}</span>
        </button>`;
    }).join("");
    sceneMount.appendChild(plotLayer);
  }

  /* --- Legend ------------------------------------------------------------ */

  const legend = qs("[data-radar-legend]", root);
  if (legend) {
    legend.innerHTML = RINGS.map(
      (ring) =>
        `<li><i style="color:${ring.id === "adopt" ? "#5fd4c4" : "#d8a657"}"></i>${ring.label}<span class="visually-hidden"> — ${ring.body}</span></li>`
    ).join("");
  }

  /* --- List -------------------------------------------------------------- */

  const list = qs("[data-trend-list]", root);
  if (list) list.innerHTML = TRENDS.map(trendMarkup).join("");

  const plots = qsa("[data-plot]", root);
  const items = qsa("[data-trend]", list || root);

  /* --- Cross-highlighting ------------------------------------------------ */

  const highlight = (id) => {
    plots.forEach((plot) => plot.classList.toggle("is-active", plot.getAttribute("data-plot") === id));
    items.forEach((item) => item.classList.toggle("is-active", item.getAttribute("data-trend") === id));
  };

  const clear = () => highlight(null);

  const disposers = [];

  plots.forEach((plot) => {
    const id = plot.getAttribute("data-plot");
    disposers.push(on(plot, "pointerenter", () => highlight(id)));
    disposers.push(on(plot, "focus", () => highlight(id)));
    disposers.push(on(plot, "pointerleave", clear));
    disposers.push(on(plot, "blur", clear));
    disposers.push(
      on(plot, "click", () => {
        const target = items.find((item) => item.getAttribute("data-trend") === id);
        target?.scrollIntoView({ behavior: "smooth", block: "center" });
        highlight(id);
      })
    );
  });

  items.forEach((item) => {
    const id = item.getAttribute("data-trend");
    disposers.push(on(item, "pointerenter", () => highlight(id)));
    disposers.push(on(item, "pointerleave", clear));
  });

  /* --- Motion ------------------------------------------------------------ */

  const mm = createMotionContext();

  mm.add(MEDIA.reduce, () => {});

  mm.add(MEDIA.any, () => {
    const disposePlots = watchReveal(plots, {
      from: { autoAlpha: 0, scale: 0.4 },
      to: { autoAlpha: 1, scale: 1 },
      duration: 0.55,
      ease: "back.out(1.6)",
      stagger: { each: 0.06, from: "random" },
      start: "top 84%",
    });

    const disposeItems = watchReveal(items, {
      from: { autoAlpha: 0, y: 16 },
      duration: 0.5,
      stagger: 0.04,
      start: "top 84%",
    });

    return () => {
      disposePlots();
      disposeItems();
    };
  });

  return () => {
    disposers.forEach((dispose) => dispose());
    mm.revert();
  };
}
